import {
  type EventClickName,
  type PIClickName,
  type Slide,
  SlideType,
  type TrackingClickName
} from './video-slideshow.types';
import {videoConfig} from './video-config';
import {PubSub} from '@mamdev/cad.static-next-lib';
import Tracking2 from '../../ts/services/tracking2';
import {type EventData, type TealiumViewParams, type VideoInfo} from '../../ts/services/tracking2/interface';

interface BaseTrackingData {
  clickname?: string;
  piUrl: string;
}

export interface EventTrackingData extends BaseTrackingData {
  slidenumber: number;
}

export interface PIData extends BaseTrackingData {
  piUrl: string;
  click1: string;
  click2: string;
  click3: string;
  click4: string;
  referrer: 'undefined';
  slidenumber: number;
  clickname: string;
}

export interface TrackingData extends BaseTrackingData {
  autoplay: boolean;
  muted: boolean;
  fullScreen: boolean;
  referrer: string;
  click1: string;
  click2: string;
  click3: string;
  click4: string;
  videoaction: string;
  videoposition: string;
  videoduration: number;
}

export const resultStateMappings = {
  'description-open': 'expanded',
  'description-close': 'collapsed',
  'video-mute': 'muted',
  'video-pause': 'paused',
  'video-play': 'played',
  'video-slideshow-close': 'closed',
  'video-unmute': 'unmuted',
  'content-0-watched': 'contentView_0',
  'content-25-watched': 'contentView_25',
  'content-50-watched': 'contentView_50',
  'content-75-watched': 'contentView_75',
  'content-100-watched': 'contentView_100',
  'video-slideshow-prev': 'prev_video',
  'video-slideshow-next': 'next_video'
};

/**
 * Provides tracking functionality for the vertical slideshow module.
 */
export class VideoSlideshowTracking {
  private initialPISent = false;

  generateEventData(clickname: EventClickName, slideIndex: number): EventTrackingData {
    return {
      piUrl: this.setCorrectUrlCategory(window.ui.brain.eventUrl).toString(),
      clickname: clickname,
      slidenumber: slideIndex
    };
  }

  generatePiData(clickname: string, slide: Slide, slideIndex: number): PIData {
    let piUrl: URL;

    if (slide.type === SlideType.video) {
      piUrl = new URL(atob(slide.brainVidPiUrl));
    } else {
      // For ad slides, use the PI URL of the page, but set the category to "videoslideshow".
      piUrl = this.setCorrectUrlCategory(window.ui.brain.piUrl);

      // consoleLog('ad slide PI URL', piUrl, piUrl.toString());
    }

    return {
      piUrl: piUrl?.toString(),
      click1: 'undefined',
      click2: 'undefined',
      click3: 'undefined',
      click4: 'undefined',
      referrer: 'undefined',
      slidenumber: slideIndex,
      clickname: clickname
    };
  }

  generateTrackingData(clickname: TrackingClickName, slide: Slide, slideIndex: number, videoDuration: number): TrackingData {
    return {
      piUrl: atob(slide.brainVidUrl),
      autoplay: videoConfig.autostart,
      muted: videoConfig.mute,
      fullScreen: videoConfig.allowFullscreen,
      referrer: '&referrer=undefined',
      click1: '&click1=undefined',
      click2: '&click2=undefined',
      click3: '&click3=undefined',
      click4: '&click4=undefined',
      videoaction: '&videoaction=' + clickname,
      videoposition: 'slideshow-video-' + slideIndex,
      videoduration: videoDuration
    };
  }

  sendEventPixel(clickname: EventClickName, slide: Slide, slideIndex: number, origin?: string) {
    PubSub.publish('video-slideshow:setEventPixel', this.generateEventData(clickname, slideIndex));
    this.sendTracking2EventPixel(clickname, slide, origin);
  }

  sendPI(clickname: PIClickName, slide: Slide, slideIndex: number) {
    try {
      // console.log('[video-slideshow-tracking] Send slide PI', slideIndex, clickname, slide, slide.brainVidPiUrl);

      if (!slide) return;

      const piData = this.generatePiData(clickname, slide, slideIndex);

      PubSub.publish('video-slideshow:setPixel', piData);

      this.sendTracking2VirtualPI(clickname, slide, slideIndex);
    } catch (e) {
      console.error(e);
    }
  }

  sendTracking2VirtualPI(clickname: 'video-slideshow-next' | 'video-slideshow-prev', slide: Slide, slideIndex: number) {
    let tracking2parameters: TealiumViewParams;

    if (this.initialPISent) {
      tracking2parameters = {
        newAgof: slide.tracking.agof,
        newBusinessEventType: 'userAction',
        newComponentName: 'vertical-video',
        newComponentPath: clickname === 'video-slideshow-next' ? '.next' : '.previous',
        newContentName: slide.contentName,
        newContentType: 'verticalvideo',
        newEventType: 'swipe',
        newPageType: 'article'
      };
    } else {
      tracking2parameters = {
        newAgof: slide.tracking.agof,
        newContentName: slide.contentName,
        newContentType: 'verticalvideo',
        newPageType: 'article'
      };
      this.initialPISent = true;
    }

    // console.log('[video-slideshow-tracking] send Tracking 2 virtual PI', tracking2parameters, slideIndex.toString(), this.initialPISent);

    if (window.utag_data && slide.coremId) {
      window.utag_data.articleId = this.generateArticleIdTrackingParameter(slide.coremId);
      window.utag_data.subContentIndex = slideIndex.toString();
      Tracking2.callTealiumView(tracking2parameters);
      Tracking2.callTealiumLink(tracking2parameters as EventData);
    }
  }

  sendTrackingPixel(clickname: TrackingClickName, slide: Slide, slideIndex: number, videoDuration: number, origin = '') {
    const trackingData = this.generateTrackingData(clickname, slide, slideIndex, videoDuration);

    // console.log('[video-slideshow-tracking] check if clickname is in resultmappings: ', clickname, slide, origin, trackingData);
    PubSub.publish('video-slideshow:setPixel', trackingData);

    if (clickname in resultStateMappings) {
      // console.log('[video-slideshow-tracking] clickname in resultmappings: ' + clickname, slide, origin, trackingData);
      this.sendTracking2EventPixel(clickname, slide, origin, trackingData);
    }
  }

  sendTracking2EventPixel(clickname: EventClickName | TrackingClickName | PIClickName, slide: Slide, origin?: string, trackingData?: TrackingData) {
    if (!this.isValidTracking2EventClickname(clickname)) return;

    // console.log('[video-slideshow-tracking] send Tracking 2.0 event', clickname);

    Tracking2.callTealiumLink({
      articleId: (slide && slide.type === SlideType.video) ? this.generateArticleIdTrackingParameter(slide.coremId) : '',
      businessEventType: 'videoplay',
      componentName: 'vertical-video',
      componentPath: origin ? `vertical-video.${origin}` : 'vertical-video',
      eventType: 'click',
      componentResultState: resultStateMappings[clickname],
      videoInfo: this.getVideoInfo(resultStateMappings[clickname], slide, trackingData)
    });
  }

  getVideoInfo(clickname: string, slide: Slide, trackingData?: TrackingData): VideoInfo {
    return {
      articleId: (slide && slide.type === SlideType.video) ? this.generateArticleIdTrackingParameter(slide.coremId) : '',
      contentCategory: slide?.tracking?.contentCategory ?? '',
      duration: slide ? slide.duration : undefined,
      position: 'vertical-video',
      provider: slide?.tracking?.contentProvider ?? '',
      teaserHeadline: slide?.tracking?.teaserHeadline ?? '',
      videoState: {
        currentTime: undefined, // optional
        videoEventType: clickname,
        properties: {
          fullscreen: trackingData?.fullScreen ?? false,
          expanded: false, // "enterpictureinpicture" not possible for vertical
          autoplay: trackingData?.autoplay ?? false,
          sticky: false, // this.floating not possible for verticals
          muted: trackingData?.muted ?? false
        },
        phase: undefined // optional
      }
    };
  }

  isValidTracking2EventClickname(clickname: string): clickname is keyof typeof resultStateMappings {
    // console.log('[video-slideshow-tracking] isValidClickname', clickname, clickname in resultStateMappings);
    return clickname in resultStateMappings;
  }

  setCorrectUrlCategory(baseUrl: string) {
    const correctCategory = 'videoslideshow';
    const piUrl = new URL(baseUrl);
    piUrl.searchParams.set('category', correctCategory);

    const nameParameter = piUrl.searchParams.get('name');

    if (nameParameter) {
      const nameArray = nameParameter.split('.');
      nameArray[3] = correctCategory;
      piUrl.searchParams.set('name', nameArray.join('.'));
    }

    return piUrl;
  }

  /**
   * Generates the Article ID string used as a tracking parameter with the format "cmd:slideID".
   * @param slideID
   */
  generateArticleIdTrackingParameter(slideID: number) {
    return `cmd:${slideID}`;
  }
}
